import { notify } from '@affine/component';
import { markBackendAvailable } from '@affine/core/utils/backend-availability';
import { DebugLogger } from '@affine/debug';
import { UserFriendlyError } from '@affine/error';
import { I18n } from '@affine/i18n';
import { fromPromise, Service } from '@toeverything/infra';

import {
  gatedBackendFetch,
  isExplicitBackendRequestCancellation,
} from './backend-request-gate';
import type { WorkspaceRequestMode } from './request-coordinator';
import type { ServerService } from './server';

const logger = new DebugLogger('affine:fetch');

export type FetchInit = RequestInit & {
  timeout?: number;
  streamlainMode?: WorkspaceRequestMode;
};

export class FetchService extends Service {
  private static rateLimitWarningShown = 0;

  /**
   * Debounced rate limit warning — shows at most once per 10 seconds.
   */
  static emitRateLimitWarning(): void {
    const now = Date.now();
    if (now - FetchService.rateLimitWarningShown < 10_000) return;
    FetchService.rateLimitWarningShown = now;
    notify.warning({
      title: I18n['error.TOO_MANY_REQUEST'](),
      message: I18n['com.streamlain.error.too-many-requests.message'](),
    });
  }

  constructor(private readonly serverService: ServerService) {
    super();
  }
  rxFetch = (
    input: string,
    init?: RequestInit & {
      // https://github.com/microsoft/TypeScript/issues/54472
      priority?: 'auto' | 'low' | 'high';
    } & {
      traceEvent?: string;
    }
  ) => {
    return fromPromise(signal => {
      return this.fetch(input, { signal, ...init });
    });
  };

  /**
   * fetch with custom custom timeout and error handling.
   */
  fetch = async (input: string, init?: FetchInit): Promise<Response> => {
    logger.debug('fetch', input);
    const externalSignal = init?.signal;
    if (externalSignal?.aborted) {
      throw externalSignal.reason;
    }
    const abortController = new AbortController();
    externalSignal?.addEventListener('abort', reason => {
      abortController.abort(reason);
    });

    const timeout = init?.timeout ?? 15000;
    const timeoutId =
      timeout > 0
        ? setTimeout(() => {
            abortController.abort(new Error('timeout after ' + timeout + 'ms'));
          }, timeout)
        : undefined;

    let res: Response;
    try {
      res = await gatedBackendFetch(
        input,
        {
          ...init,
          signal: abortController.signal,
          headers: {
            ...init?.headers,
            'x-streamlain-version': BUILD_CONFIG.appVersion,
          },
        },
        {
          baseUrl: this.serverService.server.serverMetadata.baseUrl,
          isCancellation: error =>
            isExplicitBackendRequestCancellation(error, externalSignal),
        }
      );
    } catch (error) {
      clearTimeout(timeoutId);
      if (externalSignal?.aborted) {
        throw externalSignal.reason;
      }
      throw this.networkError(error);
    }
    clearTimeout(timeoutId);

    if (!res.ok) {
      // Receiving an HTTP response proves that the backend transport is
      // reachable. Scoped 5xx responses are handled by their request owner.
      markBackendAvailable();
      if (res.status === 429) {
        // Rate limited — log once, suppress error flood
        const retryAfter = res.headers.get('Retry-After');
        const remaining = res.headers.get('X-RateLimit-Remaining');
        logger.debug(
          `Rate limited (429). Retry-After: ${retryAfter ?? 'unknown'}s, Remaining: ${remaining ?? 'unknown'}`
        );
        if (init?.streamlainMode === 'foreground') {
          FetchService.emitRateLimitWarning();
        }
        throw new UserFriendlyError({
          status: 429,
          code: 'TOO_MANY_REQUEST',
          type: 'TOO_MANY_REQUEST',
          name: 'TOO_MANY_REQUEST',
          message: I18n['com.streamlain.error.too-many-requests.message'](),
        });
      } else if (res.status === 504) {
        const error = new Error('Gateway Timeout');
        logger.debug('network error', error);
        throw new UserFriendlyError({
          status: 504,
          code: 'NETWORK_ERROR',
          type: 'NETWORK_ERROR',
          name: 'NETWORK_ERROR',
          message: I18n['com.streamlain.error.gateway-timeout.message'](),
          stacktrace: error.stack,
        });
      } else {
        if (res.headers.get('Content-Type')?.startsWith('application/json')) {
          throw UserFriendlyError.fromAny(await res.json());
        } else {
          throw UserFriendlyError.fromAny(await res.text());
        }
      }
    }

    markBackendAvailable();
    return res;
  };

  private networkError(error: unknown): UserFriendlyError {
    return new UserFriendlyError({
      status: 504,
      code: 'NETWORK_ERROR',
      type: 'NETWORK_ERROR',
      name: 'NETWORK_ERROR',
      message: I18n['com.streamlain.error.network.message'](),
      stacktrace: error instanceof Error ? error.stack : undefined,
    });
  }
}
